import { INTAKE_STAGE } from "@/models/intakeStatus";
import { openStepOf, stepRoute } from "@/models/intakeSteps";
import { buildStatusNotesPayload } from "@/utils/statusNotes";
import { lockHolderName } from "@/utils/lockDisplay";
function formatProviderName(form) {
    if (!form)
        return "";
    const first = form.providerFirstName || "";
    const last = form.providerLastName || "";
    return `${first} ${last}`.trim();
}
function formatPatientName(item, isDocument) {
    const fallback = "Unknown Patient";
    const source = isDocument
        ? item.patient || item.patientGuess
        : item.patient;
    if (!source)
        return fallback;
    const name = `${source.firstName || ""} ${source.lastName || ""}`.trim();
    return name || fallback;
}
function buildStatusNotes(notes) {
    return buildStatusNotesPayload(notes);
}
function buildNavigateTo(item, isDocument) {
    if (isDocument) {
        const doc = item;
        const readOnly = doc.status !== INTAKE_STAGE.INTAKE;
        return { name: "intake-review", params: { id: doc.key }, query: readOnly ? { ro: "1" } : {} };
    }
    const order = item;
    const step = openStepOf(order.status);
    if (step)
        return stepRoute(step, order.id);
    switch (order.status) {
        case INTAKE_STAGE.PRIOR_AUTHORIZATION:
            return order.orderInfo ? { name: "check-my-coverage", params: { id: order.orderInfo } } : null;
        case INTAKE_STAGE.DENIAL_FOLLOWUP:
            return order.orderInfo
                ? { name: "check-my-coverage", params: { id: order.orderInfo }, query: { section: "appeal" } }
                : null;
        case INTAKE_STAGE.ARCHIVED:
            return stepRoute("benefits_verification", order.id, true);
        default:
            return stepRoute("benefits_verification", order.id);
    }
}
/**
 * Unified row shape for the intake table. Wraps either an IntakeDocument
 * or an IntakeOrder payload from the `combined-feed/` endpoint and exposes
 * the display/derived fields the table renders.
 */
export class IntakeFeedRow {
    id;
    type;
    patientName;
    mrn;
    status;
    dateReceived;
    dateModified;
    timeInStage;
    insurance;
    medication;
    provider;
    providerPhone;
    providerPhoneExtension;
    documentTypes;
    sentBy;
    // Mutable — callers optimistically patch this in place when tags are edited.
    statusTags;
    // Read-only roster-derived chips; the server projects the tenant's substatus definitions per row.
    substatusTags;
    tagDueDates;
    // Mutable — callers optimistically patch this in place when assignees are edited.
    assignees;
    assignedGroups;
    automationTasks;
    isSnoozed;
    snoozeUntil;
    // Mutable — the list page's lock poll patches these in place through applyLock.
    isLocked = false;
    lockedBy = null;
    lockedByUserId = null;
    // Mutable — the row menu marks the case read or unread in place.
    isUnread;
    hasInsuranceCard;
    sourceDocumentKey;
    isArchived;
    statusNotes;
    hasNotes;
    navigateTo;
    raw;
    modifiedBy;
    constructor(item, type) {
        const isDocument = type === "document";
        this.type = type;
        this.raw = item;
        this.status = item.status;
        this.dateReceived = item.createdOn ?? null;
        this.dateModified = item.modifiedOn ?? null;
        this.timeInStage = item.statusChangedAt ?? item.createdOn ?? null;
        this.patientName = formatPatientName(item, isDocument);
        this.mrn = item.patient?.externalId || "";
        this.statusTags = item.statusTags ?? [];
        this.substatusTags = item.substatusTags ?? [];
        this.tagDueDates = item.tagDueDates ?? [];
        this.assignees = item.assignees ?? [];
        this.assignedGroups = item.assignedGroups ?? [];
        this.snoozeUntil = item.snoozeUntil ?? null;
        this.isSnoozed = !!item.snoozeUntil && new Date(item.snoozeUntil) > new Date();
        this.applyLock(item.lock);
        this.isUnread = !!item.isUnread;
        this.isArchived = item.status === INTAKE_STAGE.ARCHIVED;
        this.statusNotes = buildStatusNotes(item.statusNotes);
        this.hasNotes = !!this.statusNotes;
        this.navigateTo = buildNavigateTo(item, isDocument);
        this.modifiedBy = item.modifiedByName ?? "";
        if (isDocument) {
            const doc = item;
            this.id = doc.key;
            this.insurance = doc.insuranceName ?? "";
            this.medication = doc.medicationName ?? "";
            this.provider = formatProviderName(doc.intakeOrderForms?.[0]);
            this.providerPhone = doc.intakeOrderForms?.[0]?.providerPhone ?? "";
            this.providerPhoneExtension = doc.intakeOrderForms?.[0]?.providerPhoneExtension ?? "";
            this.documentTypes = (doc.documentTypes ?? []).join(", ");
            this.sentBy = doc.submittedByName ?? "";
            this.automationTasks = doc.automationTasks ?? [];
            this.hasInsuranceCard = false;
            this.sourceDocumentKey = null;
        }
        else {
            const order = item;
            this.id = order.id;
            this.insurance = order.insuranceName ?? order.insuranceCard?.payorName ?? "";
            this.medication = order.orderForm?.therapyAdministration?.[0]?.name ?? "";
            this.provider = formatProviderName(order.orderForm);
            this.providerPhone = order.orderForm?.providerPhone ?? "";
            this.providerPhoneExtension = order.orderForm?.providerPhoneExtension ?? "";
            this.documentTypes = "";
            this.sentBy = "";
            this.automationTasks = [];
            this.hasInsuranceCard = !!order.hasInsuranceCard;
            this.sourceDocumentKey = order.orderForm?.intakeDocumentKey ?? null;
        }
    }
    /** Set the lock display fields from one payload; a null lock clears all three. */
    applyLock(lock) {
        this.isLocked = !!lock;
        this.lockedBy = lockHolderName(lock);
        this.lockedByUserId = lock?.userPk ?? null;
    }
    /** Build a row from a `{intakeDocument, intakeOrder}` combined-feed envelope. */
    static fromCombinedFeedRow(row) {
        const item = row.intakeDocument || row.intakeOrder;
        return item ? new IntakeFeedRow(item, row.intakeDocument ? "document" : "order") : null;
    }
}
